import { notification } from "antd"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { getBudget, getBudgets, getBudgetYears, updateBudget, updateProjectItemPricing } from "./budgetApi"
import { Budget } from "./budgetModel"
import { hasValue } from "../../helpers/nullable"

export const useBudgetYears = () => {
  return useQuery(['budget-years'], getBudgetYears)
}

export const useBudget = (year: number | null) => {
  return useQuery(['budgets', year], () => getBudget(year!), { enabled: hasValue(year) })
}

export const useBudgetsInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidateAll: () => {
      queryClient.invalidateQueries(['budgets'])
      queryClient.invalidateQueries(['budget-years'])
    }
  }
}

type UpdateBudgetArgs = {
  budgets: Budget[]
  updateJobs: boolean
}

export const useUpdateBudget = (onSuccess: () => void) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ budgets, updateJobs }: UpdateBudgetArgs) => {
      await updateBudget(budgets)
      if (updateJobs) {
        await updateProjectItemPricing(budgets[0].year)
      }
    },
    onSuccess: (_, { budgets, updateJobs }) => {
      queryClient.invalidateQueries(['budgets', budgets[0].year])
      if (updateJobs) {
        notification.success({ message: 'Successfully updated item pricing for unsold jobs assigned to this budget.' })
      }
      onSuccess()
    },
    onError: (error: any) => {
      const message = error.response?.data?.message ?? 'There was an error updating the yearly budget.'
      notification.error({ message })
    }
  })
}

export const useBudgets = () => {
  return useQuery(['budgets'], getBudgets, {
    staleTime: 30 * 1000
  })
}
